import type { NodeViewProps } from '@tiptap/react';

import { cn } from '@repo/lib/cn';
import { useCallback, useRef, useState } from 'react';

import type { ResizableButtonProps } from '.';

export function useResizableMedia<T extends HTMLElement>(nodeComponentProps: NodeViewProps, getNaturalSize: (el: T) => { width: number; height: number }) {
  const { updateAttributes } = nodeComponentProps;
  const ref = useRef<T>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [originalSize, setOriginalSize] = useState({ width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const handleMediaLoad = useCallback(() => {
    if (ref.current) {
      setOriginalSize(getNaturalSize(ref.current));
    }
  }, [getNaturalSize]);

  const getConstrainedDimensions = useCallback(
    (newWidth: number) => {
      if (!ref.current) return { width: newWidth, height: 'auto' };

      const aspectRatio: number = originalSize.width / originalSize.height;
      const constrainedWidth: number = Math.max(80, Math.min(newWidth));
      const constrainedHeight: number = constrainedWidth / aspectRatio;

      return { width: constrainedWidth, height: constrainedHeight };
    },
    [originalSize]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, side: 'left' | 'right') => {
      e.preventDefault();
      setIsDragging(true);

      const startX = e.clientX;
      const startWidth = ref.current?.offsetWidth || 0;

      const handleMouseMove = (e: MouseEvent) => {
        const deltaX: number = e.clientX - startX;
        const newWidth: number = side === 'right' ? startWidth + deltaX : startWidth - deltaX;

        if (newWidth > 80) {
          // Minimum width
          const { width, height } = getConstrainedDimensions(newWidth);
          updateAttributes({
            maxWidth: Math.round(width),
            maxHeight: typeof height === 'number' ? Math.round(height) : height,
          });
        }
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [updateAttributes, getConstrainedDimensions]
  );

  return {
    ref,
    containerRef,
    originalSize,
    handleMediaLoad,
    handleMouseDown,
    isDragging,
  };
}

export function ResizableButton(resizableButtonProps: ResizableButtonProps) {
  const { handleMouseDown, side, className, ...props } = resizableButtonProps;

  return (
    <div
      className={cn(
        'bg-fill-brand hover:bg-fill-brand-hover absolute top-1/2 flex h-12 w-3 -translate-y-1/2 cursor-ew-resize items-center justify-center rounded-sm',
        side === 'left' ? 'left-0.5' : 'right-0.5',
        className
      )}
      {...props}
      onMouseDown={(e) => handleMouseDown(e, side)}
    >
      <div className="bg-text-primary h-6 w-1 rounded-full opacity-60" />
    </div>
  );
}
